import { yupResolver } from '@hookform/resolvers/yup'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ReactElement, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useClient } from 'urql'
import { object, string } from 'yup'

import { Spinner } from '@/components/Spinner'
import { SignUpLayout } from '@/components/layout/web'
import { RubyText } from '@/components/ruby-text'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BackButton } from '@/components/web-back-button'
import { PasswordForgotMutation } from '@/queries'
import Link from '@/update-monitor/Link'

type FormData = {
  email: string
}

const schema = object({
  email: string()
    .email('メールアドレスのフォーマットが不正です')
    .required('メールアドレスを入力してください'),
})

export default function PasswordForgot() {
  const {
    register,
    watch,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
  })
  const client = useClient()
  const router = useRouter()

  const [submitting, setSubmitting] = useState<boolean>(false)
  const submittingRef = useRef<boolean>(false)

  const handleSendResetPasswordEmail = async () => {
    if (submittingRef.current) return
    setSubmitting(true)
    submittingRef.current = true

    const email = getValues('email')
    const { data, error } = await client.mutation(PasswordForgotMutation, {
      input: { email },
    })

    if (!error && !data?.passwordForgot?.errors?.length) {
      setSubmitting(false)
      submittingRef.current = false
      router.push({ pathname: '/web/password_reset', query: { email } })
      return
    }

    toast.error('認メールの送信に失敗しました', {
      toastId: 'send-email-failed',
      position: 'bottom-right',
    })
    setSubmitting(false)
    submittingRef.current = false
  }

  return (
    <>
      <Head>
        <title>ロジックラボ | パスワード再設定リクエスト</title>
      </Head>

      <div className="relative">
        <BackButton className="left-2 top-1 md:left-5 md:top-4" />
      </div>

      <div className="flex grow flex-col items-center px-5 pt-[40px] md:pt-[73px]">
        <div className="mx-auto w-full max-w-[940px] rounded-lg bg-white px-4 pb-[30px] pt-[35px] md:rounded-xl md:pb-[60px] md:pt-[50px]">
          <div className="mx-auto w-full md:max-w-[400px]">
            <h3 className="mb-4 text-center text-[18px] font-bold md:mb-6 md:text-2xl">
              パスワードを忘れた場合
            </h3>
            <p className="mb-[30px] text-center text-xs md:mb-[50px] md:text-[15px] md:leading-normal">
              <RubyText>
                登録されているメールアドレスを入力してください。パスワード再設定用のメールをお送りします。
              </RubyText>
            </p>

            <div>
              <Label htmlFor="email" className="g-label mb-[6px]">
                メールアドレス
              </Label>
              <Input
                id="email"
                type="text"
                error={!!errors.email}
                placeholder="メールアドレス"
                {...register('email')}
              />
              {!!errors.email ? (
                <p className="mt-1 text-xs text-red-500">
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            <Button
              type="button"
              variant="secondary"
              className="mx-auto mt-[38px] flex w-[270px]"
              onClick={handleSendResetPasswordEmail}
              disabled={!watch('email') || !!errors.email}
            >
              {submitting ? <Spinner /> : '確認メールを送る'}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

const RightButton = (): JSX.Element => {
  return (
    <Button
      variant="light"
      className="h-[38px] w-[75px] px-3 py-2.5 text-xs md:h-[46px] md:w-[100px]"
      shadow={false}
      asChild={true}
    >
      <Link href="/web/sign_in">ログイン</Link>
    </Button>
  )
}

PasswordForgot.getLayout = (page: ReactElement) => (
  <SignUpLayout headerRightNode={<RightButton />}>{page}</SignUpLayout>
)
